import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Box,
    Badge,
    Checkbox,
} from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import type { CoordinationIssueStatus } from '../../state/useWorksharingStore';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';

const statusColor = (status: CoordinationIssueStatus): string => {
    switch (status) {
        case 'New': return 'status.error';
        case 'Postponed': return 'status.warning';
        case 'Resolved': return 'status.success';
        case 'Rejected': return 'text.secondary';
    }
};

/**
 * Coordination Review dialog (Collaborate > Coordinate > Coordination Review).
 * Lists Copy/Monitor change notifications and lets the user Accept (resolve),
 * Postpone or Reject each pending issue — the BIM decision workflow.
 */
export function CoordinationReviewDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'coordinationReview');
    const onClose = useAppStore((state) => state.closeCollabDialog);
    const issues = useWorksharingStore((s) => s.coordinationIssues);
    const refresh = useWorksharingStore((s) => s.refreshCoordinationIssues);
    const setIssueStatusBulk = useWorksharingStore((s) =>s.setIssueStatusBulk); const [showResolved, setShowResolved] = useState(false); const [checked, setChecked] = useState<Set<string>>(new Set());

    const visible = useMemo(
        () => issues.filter((i) => (showResolved ? true : i.status === 'New' || i.status === 'Postponed')),
        [issues, showResolved]
    );
    const pending = visible.filter((i) => i.status === 'New' || i.status === 'Postponed');
    const decidedIds = pending.map((i) => i.id);

    const toggle = (id: string) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const decideAll = (status: CoordinationIssueStatus) => {
        setIssueStatusBulk(decidedIds, status);
        setChecked(new Set());
    };

    const decideChecked = (status: CoordinationIssueStatus) => {
        if (checked.size === 0) return;
        setIssueStatusBulk(Array.from(checked), status);
        setChecked(new Set());
    };

    return (
        <ManageDialogFrame
            title={t('collab.coordinationReview')}
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            footer={
                <HStack spacing={2} w="full" justify="space-between">
                    <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<RefreshCw size={13} />}
                        onClick={() => { refresh(); }}
                    >{t('collab.checkNotifications')}</Button>
                    <ManageDialogCloseFooter onClose={onClose} />
                </HStack>
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        {pending.length} pending notification(s). Resolve, postpone or reject changes
                        detected in monitored models.
                    </Text>
                    <Checkbox size="sm" isChecked={showResolved} onChange={(e) => setShowResolved(e.target.checked)}>
                        <Text fontSize="xs" whiteSpace="nowrap">{t('collab.showResolvedRejected')}</Text>
                    </Checkbox>
                </HStack>

                <Box
                    borderWidth="1px"
                    borderColor="var(--color-border)"
                    borderRadius="var(--cad-radius-sm)"
                    maxH="360px"
                    overflowY="auto"
                >
                    {visible.length === 0 ? (
                        <Text fontSize="sm" color="var(--color-text-secondary)" p={4} textAlign="center">{t('collab.noNotificationsCopyMonitorElementsAndClickCheckNotifications')}</Text>
                    ) : (
                        visible.map((issue) => (
                            <HStack
                                key={issue.id}
                                px={2}
                                py={1.5}
                                spacing={2}
                                borderBottom="1px solid var(--color-border)"
                            >
                                {issue.status === 'New' || issue.status === 'Postponed' ? (
                                    <Checkbox size="sm" isChecked={checked.has(issue.id)} onChange={() => toggle(issue.id)} />
                                ) : (
                                    <Box w="18px" flexShrink={0} />
                                )}
                                <Badge flexShrink={0} colorScheme="purple" variant="subtle">{issue.kind}</Badge>
                                <Box flex="1 1 auto" minW={0}>
                                    <Text fontSize="sm" fontWeight="medium" isTruncated>
                                        {issue.name} <Text as="span" color="var(--color-text-secondary)">· {issue.monitorName}</Text>
                                    </Text>
                                    <Text fontSize="xs" color="var(--color-text-secondary)" isTruncated>{issue.description}</Text>
                                </Box>
                                <HStack
                                    flexShrink={0}
                                    spacing={0}
                                    px={1}
                                    py={0.5}
                                    borderRadius="var(--cad-radius-sm)"
                                    borderWidth="1px"
                                    borderColor="var(--color-border)"
                                    fontSize="xs"
                                    color={statusColor(issue.status)}
                                >
                                    <Box w="6px" h="6px" borderRadius="full" bg={statusColor(issue.status)} />
                                    <Text ml={1}>{issue.status}</Text>
                                </HStack>
                                {(issue.status === 'New' || issue.status === 'Postponed') && (
                                    <HStack flexShrink={0} spacing={1}>
                                        <Button size="xs" colorScheme="green" variant="solid" onClick={() => setIssueStatusBulk([issue.id], 'Resolved')}>{t('collab.resolve')}</Button>
                                        <Button size="xs" colorScheme="yellow" variant="outline" onClick={() => setIssueStatusBulk([issue.id], 'Postponed')}>{t('collab.postpone')}</Button>
                                        <Button size="xs" colorScheme="red" variant="outline" onClick={() => setIssueStatusBulk([issue.id], 'Rejected')}>{t('collab.reject')}</Button>
                                    </HStack>
                                )}
                            </HStack>
                        ))
                    )}
                </Box>

                <HStack spacing={2} justify="flex-end">
                    <Text fontSize="xs" color="var(--color-text-secondary)" mr="auto">
                        {checked.size > 0 ? `${checked.size} selected` : 'Bulk actions apply to all pending items'}
                    </Text>
                    <Button size="xs" onClick={() => decideChecked('Resolved')} isDisabled={checked.size === 0}>{t('collab.resolveSelected')}</Button>
                    <Button size="xs" onClick={() => decideAll('Resolved')} isDisabled={pending.length === 0}>{t('collab.resolveAll')}</Button>
                    <Button size="xs" onClick={() => decideAll('Postponed')} isDisabled={pending.length === 0}>{t('collab.postponeAll')}</Button>
                    <Button size="xs" onClick={() => decideAll('Rejected')} isDisabled={pending.length === 0}>{t('collab.rejectAll')}</Button>
                </HStack>
            </VStack>
        </ManageDialogFrame>
    );
}
